import palette from '../data/palette.json';
import { Cartridge, Mirroring } from '../interfaces/cartridge';
import { Logger } from '../util/logger';

/**
 * Ricoh 2C02 PPU — dot/scanline-timed implementation.
 *
 * Driven by step(cpuCycles) from the master loop in nes.ts; each CPU cycle is
 * 3 PPU dots. Frame layout (NTSC): pre-render line -1, visible 0–239,
 * post-render 240, VBlank 241–260; 341 dots per line (340 on odd frames when
 * rendering is enabled). NMI is edge-triggered on (vblank && NMI-enable).
 *
 * Contract (see AGENTS.md): constructor(cartridge, onNmi), step, read(0-7),
 * write(0-7, v), getFrameBuffer(), getFrameCount(), reset().
 * No browser APIs in this file.
 */

// Loopy register bit masks
const COARSE_X = 0x001F;
const COARSE_Y = 0x03E0;
const NAMETABLE = 0x0C00;
const FINE_Y = 0x7000;
const HORIZONTAL_BITS = 0x041F; // coarse X + nametable X
const VERTICAL_BITS = 0x7BE0;   // coarse Y + fine Y + nametable Y

export class Ppu2C02 {
    private cartridge: Cartridge;
    private onNmi: () => void;
    private logger: Logger;

    // CPU-visible registers
    private ctrl: number = 0;    // $2000
    private mask: number = 0;    // $2001
    private status: number = 0;  // $2002 (bits 7/6/5 meaningful)
    private oamAddr: number = 0; // $2003
    private openBus: number = 0; // decayed bus value; used for $2002 low bits

    // Loopy registers
    private v: number = 0; // current VRAM address (15 bits)
    private t: number = 0; // temporary VRAM address (15 bits)
    private fineX: number = 0; // fine X scroll (3 bits)
    private w: boolean = false; // write toggle

    // PPUDATA read buffer
    private readBuffer: number = 0;

    // Background pipeline
    private bgShifterLo: number = 0; // 16-bit pattern shifters
    private bgShifterHi: number = 0;
    private bgNextTileId: number = 0;
    private bgNextTileAttr: number = 0;
    private bgNextTileLo: number = 0;
    private bgNextTileHi: number = 0;
    private bgAttrShifterLo: number = 0; // 8-bit attribute shifters (fed from latch)
    private bgAttrShifterHi: number = 0;
    private bgAttrLatchLo: number = 0;
    private bgAttrLatchHi: number = 0;

    // Sprites
    private oam: Uint8Array = new Uint8Array(256);
    private static readonly MAX_SPRITES_PER_LINE = 8;
    // Precomputed sprite data for the scanline being rendered (filled by
    // evaluateSpritesForNextScanline at dot 257 of the previous line).
    private sprLinePixel: Uint8Array = new Uint8Array(256);   // 0 = transparent
    private sprLinePalette: Uint8Array = new Uint8Array(256);
    private sprLineBehindBg: Uint8Array = new Uint8Array(256); // boolean
    private sprLineIsZero: Uint8Array = new Uint8Array(256);   // boolean

    // Timing
    private scanline: number = -1; // -1 = pre-render, 0-239 visible, 240 post, 241-260 vblank
    private dot: number = 0;       // 0-340
    private frame: number = 0;
    private oddFrame: boolean = false;
    private nmiLine: boolean = false;

    // Diagnostics (read-only for tests): where sprite-0 hit last fired.
    public debugSprite0Hit: { frame: number; scanline: number; dot: number; v: number; fineX: number } =
        { frame: -1, scanline: -1, dot: -1, v: 0, fineX: 0 };
    public debugSprite0HitCountThisFrame: number = 0;

    // Memory
    private nametables: Uint8Array = new Uint8Array(0x1000); // 4KB so four-screen works
    private paletteRam: Uint8Array = new Uint8Array(32);

    // Output: RGBA, 256x240
    private frameBuffer: Uint8ClampedArray = new Uint8ClampedArray(256 * 240 * 4);

    constructor(cartridge: Cartridge, onNmi: () => void) {
        this.cartridge = cartridge;
        this.onNmi = onNmi;
        this.logger = new Logger('PPU', 1);
        this.reset();
    }

    reset(): void {
        this.ctrl = 0;
        this.mask = 0;
        this.status = 0;
        this.oamAddr = 0;
        this.openBus = 0;
        this.v = 0;
        this.t = 0;
        this.fineX = 0;
        this.w = false;
        this.readBuffer = 0;
        this.bgShifterLo = 0;
        this.bgShifterHi = 0;
        this.bgAttrShifterLo = 0;
        this.bgAttrShifterHi = 0;
        this.scanline = -1;
        this.dot = 0;
        this.frame = 0;
        this.oddFrame = false;
        this.nmiLine = false;
        this.oam.fill(0);
        this.nametables.fill(0);
        this.paletteRam.fill(0);
        this.frameBuffer.fill(0);
    }

    getFrameCount(): number {
        return this.frame;
    }

    getFrameBuffer(): Uint8ClampedArray | null {
        if (!(this.mask & 0x18)) return null; // rendering fully disabled
        return this.frameBuffer;
    }

    // ------------------------------------------------------------------
    // Master clock
    // ------------------------------------------------------------------

    step(cpuCycles: number): void {
        let dots = cpuCycles * 3;
        while (dots-- > 0) this.stepDot();
    }

    private renderingEnabled(): boolean {
        return (this.mask & 0x18) !== 0;
    }

    private stepDot(): void {
        const rendering = this.renderingEnabled();

        if (this.scanline === -1 || this.scanline < 240) {
            // Pre-render and visible scanlines: background fetch pipeline.
            if (this.scanline === -1 && this.dot === 1) {
                this.setStatusFlag(0x80, false); // clear VBlank
                this.setStatusFlag(0x40, false); // clear sprite 0 hit
                this.setStatusFlag(0x20, false); // clear sprite overflow
                this.checkNmi();
            }

            if ((this.dot >= 2 && this.dot <= 257) || (this.dot >= 321 && this.dot <= 337)) {
                // Shift background shifters one bit per dot in the fetch region.
                this.bgShifterLo = (this.bgShifterLo << 1) & 0xFFFF;
                this.bgShifterHi = (this.bgShifterHi << 1) & 0xFFFF;
                this.bgAttrShifterLo = ((this.bgAttrShifterLo << 1) & 0xFF) | this.bgAttrLatchLo;
                this.bgAttrShifterHi = ((this.bgAttrShifterHi << 1) & 0xFF) | this.bgAttrLatchHi;

                if (rendering) {
                    switch ((this.dot - 1) & 7) {
                        case 0:
                            this.loadBackgroundShifters();
                            this.bgNextTileId = this.ppuMemRead(0x2000 | (this.v & 0x0FFF));
                            break;
                        case 2: {
                            let attr = this.ppuMemRead(0x23C0 | (this.v & NAMETABLE)
                                | ((this.v >> 4) & 0x38) | ((this.v >> 2) & 0x07));
                            if (this.v & 0x0040) attr >>= 4; // coarse Y bit 1
                            if (this.v & 0x0002) attr >>= 2; // coarse X bit 1
                            this.bgNextTileAttr = attr & 0x03;
                            break;
                        }
                        case 4:
                            this.bgNextTileLo = this.ppuMemRead(((this.ctrl & 0x10) << 8)
                                + (this.bgNextTileId << 4) + ((this.v >> 12) & 0x07));
                            break;
                        case 6:
                            this.bgNextTileHi = this.ppuMemRead(((this.ctrl & 0x10) << 8)
                                + (this.bgNextTileId << 4) + ((this.v >> 12) & 0x07) + 8);
                            break;
                        case 7:
                            this.incrementScrollX();
                            break;
                    }
                }
            }

            if (rendering && this.dot === 256) this.incrementScrollY();
            if (this.dot === 257) {
                if (rendering) {
                    this.loadBackgroundShifters();
                    this.v = (this.v & ~HORIZONTAL_BITS) | (this.t & HORIZONTAL_BITS);
                }
                this.evaluateSpritesForNextScanline();
            }
            if (this.scanline === -1 && rendering && this.dot >= 280 && this.dot <= 304) {
                this.v = (this.v & ~VERTICAL_BITS) | (this.t & VERTICAL_BITS);
            }

            // MMC3-style scanline clock: A12 rise approximation at dot 260.
            if (rendering && this.dot === 260 && this.cartridge.notifyScanline) {
                this.cartridge.notifyScanline();
            }
        }

        if (this.scanline === 241 && this.dot === 1) {
            this.setStatusFlag(0x80, true); // VBlank start
            this.checkNmi();
        }

        // Emit a pixel for visible dots.
        if (this.scanline >= 0 && this.scanline < 240 && this.dot >= 1 && this.dot <= 256) {
            this.composePixel(this.dot - 1, this.scanline);
        }

        // Advance the beam. Odd frames skip the last dot of the pre-render
        // line when rendering is enabled.
        this.dot++;
        const lastDot = (this.scanline === -1 && this.oddFrame && rendering) ? 339 : 340;
        if (this.dot > lastDot) {
            this.dot = 0;
            this.scanline++;
            if (this.scanline > 260) {
                this.scanline = -1;
                this.frame++;
                this.oddFrame = !this.oddFrame;
                this.logger.log(2, () => `Frame ${this.frame} complete`);
            }
        }
    }

    private loadBackgroundShifters(): void {
        this.bgShifterLo = (this.bgShifterLo & 0xFF00) | this.bgNextTileLo;
        this.bgShifterHi = (this.bgShifterHi & 0xFF00) | this.bgNextTileHi;
        this.bgAttrLatchLo = this.bgNextTileAttr & 0x01;
        this.bgAttrLatchHi = this.bgNextTileAttr & 0x02;
    }

    // ------------------------------------------------------------------
    // Loopy scrolling helpers
    // ------------------------------------------------------------------

    private incrementScrollX(): void {
        if ((this.v & COARSE_X) === 31) {
            this.v &= ~COARSE_X;
            this.v ^= 0x0400; // flip horizontal nametable
        } else {
            this.v += 1;
        }
    }

    private incrementScrollY(): void {
        if ((this.v & FINE_Y) !== FINE_Y) {
            this.v += 0x1000;
        } else {
            this.v &= ~FINE_Y;
            let coarseY = (this.v & COARSE_Y) >> 5;
            if (coarseY === 29) {
                coarseY = 0;
                this.v ^= 0x0800; // flip vertical nametable
            } else if (coarseY === 31) {
                coarseY = 0;
            } else {
                coarseY += 1;
            }
            this.v = (this.v & ~COARSE_Y) | (coarseY << 5);
        }
    }

    // ------------------------------------------------------------------
    // Sprites
    // ------------------------------------------------------------------

    /**
     * Evaluate OAM for the scanline after the current one (hardware timing)
     * and bake the winning sprite pixel per screen X into flat arrays.
     */
    private evaluateSpritesForNextScanline(): void {
        this.sprLinePixel.fill(0);
        this.sprLineIsZero.fill(0);
        const target = this.scanline + 1;
        if (target < 0 || target >= 240) return;

        const height = (this.ctrl & 0x20) ? 16 : 8;
        let found = 0;
        for (let i = 0; i < 64; i++) {
            const oamY = this.oam[i * 4];
            const row = target - (oamY + 1);
            if (row < 0 || row >= height) continue;

            if (found === Ppu2C02.MAX_SPRITES_PER_LINE) {
                this.setStatusFlag(0x20, true); // sprite overflow (non-buggy approximation)
                break;
            }
            found++;

            const tile = this.oam[i * 4 + 1];
            const attr = this.oam[i * 4 + 2];
            const spriteX = this.oam[i * 4 + 3];
            let r = row;
            if (attr & 0x80) r = height - 1 - r; // vertical flip

            let patternBase: number;
            let tileIndex: number;
            if (height === 16) {
                patternBase = (tile & 0x01) * 0x1000;
                tileIndex = tile & 0xFE;
                if (r >= 8) { tileIndex += 1; r -= 8; }
            } else {
                patternBase = (this.ctrl & 0x08) ? 0x1000 : 0x0000;
                tileIndex = tile;
            }
            const lo = this.ppuMemRead(patternBase + tileIndex * 16 + r);
            const hi = this.ppuMemRead(patternBase + tileIndex * 16 + r + 8);
            const paletteNo = attr & 0x03;
            const behindBg = (attr & 0x20) !== 0;
            const flipH = (attr & 0x40) !== 0;
            const isZero = i === 0;

            for (let px = 0; px < 8; px++) {
                const x = spriteX + px;
                if (x > 255) break;
                if (this.sprLinePixel[x] !== 0) continue; // earlier OAM entry wins
                const bit = flipH ? px : 7 - px;
                const pixel = (((hi >> bit) & 1) << 1) | ((lo >> bit) & 1);
                if (pixel === 0) continue;
                this.sprLinePixel[x] = pixel;
                this.sprLinePalette[x] = paletteNo;
                this.sprLineBehindBg[x] = behindBg ? 1 : 0;
                this.sprLineIsZero[x] = isZero ? 1 : 0;
            }
        }
    }

    // ------------------------------------------------------------------
    // Pixel composition
    // ------------------------------------------------------------------

    private composePixel(x: number, y: number): void {
        let bgPixel = 0;
        let bgPalette = 0;
        const rendering = this.renderingEnabled();

        if (this.mask & 0x08) { // background enabled
            const bitMux = 0x8000 >> this.fineX;
            bgPixel = ((this.bgShifterHi & bitMux) ? 0x02 : 0) | ((this.bgShifterLo & bitMux) ? 0x01 : 0);
            bgPalette = ((this.bgAttrShifterHi & (0x80 >> this.fineX)) ? 0x02 : 0)
                | ((this.bgAttrShifterLo & (0x80 >> this.fineX)) ? 0x01 : 0);
            if (x < 8 && !(this.mask & 0x02)) { bgPixel = 0; bgPalette = 0; } // left clip
        }

        let sprPixel = 0;
        if ((this.mask & 0x10) && (x >= 8 || (this.mask & 0x04))) { // sprites on, not left-clipped
            sprPixel = this.sprLinePixel[x];
        }

        // Sprite 0 hit: opaque bg and sprite-0 pixels overlap, x < 255,
        // background+sprite rendering on, and not in a clipped left column.
        if (sprPixel !== 0 && this.sprLineIsZero[x] !== 0 && bgPixel !== 0 && x < 255) {
            if (x >= 8 || ((this.mask & 0x02) && (this.mask & 0x04))) {
                if ((this.status & 0x40) === 0) {
                    this.debugSprite0Hit = { frame: this.frame, scanline: this.scanline, dot: this.dot, v: this.v, fineX: this.fineX };
                    this.debugSprite0HitCountThisFrame = 1;
                } else {
                    this.debugSprite0HitCountThisFrame++;
                }
                this.setStatusFlag(0x40, true);
            }
        }

        // Priority mux
        let paletteAddr: number;
        if (!rendering) {
            // Rendering off: show backdrop, or the palette entry v points at
            // ("background palette hack").
            paletteAddr = (this.v & 0x3F00) === 0x3F00 ? (this.v & 0x1F) : 0;
        } else if (bgPixel === 0 && sprPixel === 0) {
            paletteAddr = 0;
        } else if (bgPixel !== 0 && sprPixel === 0) {
            paletteAddr = bgPalette * 4 + bgPixel;
        } else if (bgPixel === 0 && sprPixel !== 0) {
            paletteAddr = 0x10 + this.sprLinePalette[x] * 4 + sprPixel;
        } else {
            paletteAddr = this.sprLineBehindBg[x] !== 0
                ? bgPalette * 4 + bgPixel
                : 0x10 + this.sprLinePalette[x] * 4 + sprPixel;
        }

        const colorIndex = this.readPalette(paletteAddr) & ((this.mask & 0x01) ? 0x30 : 0x3F);
        const color = palette[colorIndex] || { r: 0, g: 0, b: 0 };
        const idx = (y * 256 + x) * 4;
        this.frameBuffer[idx] = color.r;
        this.frameBuffer[idx + 1] = color.g;
        this.frameBuffer[idx + 2] = color.b;
        this.frameBuffer[idx + 3] = 255;
    }

    // ------------------------------------------------------------------
    // CPU register interface (reg is 0-7, already mirrored by the bus)
    // ------------------------------------------------------------------

    read(reg: number): number {
        switch (reg & 0x07) {
            case 0x2: { // PPUSTATUS
                const value = (this.status & 0xE0) | (this.openBus & 0x1F);
                this.setStatusFlag(0x80, false); // reading clears VBlank
                this.w = false;
                this.openBus = value;
                this.checkNmi();
                return value;
            }
            case 0x4: { // OAMDATA
                const value = this.oam[this.oamAddr];
                this.openBus = value;
                return value;
            }
            case 0x7: { // PPUDATA
                let value = this.readBuffer;
                const read = this.ppuMemRead(this.v);
                if ((this.v & 0x3FFF) >= 0x3F00) {
                    value = read; // palette reads are immediate...
                    this.readBuffer = this.ppuMemRead(this.v & 0x2FFF); // ...buffer gets nametable "underneath"
                } else {
                    this.readBuffer = read;
                }
                this.v = (this.v + ((this.ctrl & 0x04) ? 32 : 1)) & 0x7FFF;
                this.openBus = value;
                return value;
            }
            default:
                return this.openBus; // write-only registers read as open bus
        }
    }

    write(reg: number, value: number): void {
        reg &= 0x07;
        value &= 0xFF;
        this.openBus = value;
        switch (reg) {
            case 0x0: // PPUCTRL
                this.ctrl = value;
                this.t = (this.t & ~NAMETABLE) | ((value & 0x03) << 10);
                this.checkNmi(); // enabling NMI during VBlank fires immediately
                break;
            case 0x1: // PPUMASK
                this.mask = value;
                break;
            case 0x3: // OAMADDR
                this.oamAddr = value;
                break;
            case 0x4: // OAMDATA
                this.oam[this.oamAddr] = value;
                this.oamAddr = (this.oamAddr + 1) & 0xFF;
                break;
            case 0x5: // PPUSCROLL
                if (!this.w) {
                    this.t = (this.t & ~COARSE_X) | (value >> 3);
                    this.fineX = value & 0x07;
                    this.w = true;
                } else {
                    this.t = (this.t & ~(COARSE_Y | FINE_Y))
                        | ((value >> 3) << 5) | ((value & 0x07) << 12);
                    this.w = false;
                }
                break;
            case 0x6: // PPUADDR
                if (!this.w) {
                    this.t = (this.t & 0x00FF) | ((value & 0x3F) << 8);
                    this.w = true;
                } else {
                    this.t = (this.t & 0x7F00) | value;
                    this.v = this.t;
                    this.w = false;
                }
                break;
            case 0x7: // PPUDATA
                this.ppuMemWrite(this.v, value);
                this.v = (this.v + ((this.ctrl & 0x04) ? 32 : 1)) & 0x7FFF;
                break;
        }
    }

    // ------------------------------------------------------------------
    // PPU memory map
    // ------------------------------------------------------------------

    private ppuMemRead(address: number): number {
        address &= 0x3FFF;
        if (address < 0x2000) {
            return this.cartridge.ppuRead(address);
        } else if (address < 0x3F00) {
            return this.nametables[this.nametableOffset(address)];
        } else {
            return this.readPalette(address & 0x1F);
        }
    }

    private ppuMemWrite(address: number, value: number): void {
        address &= 0x3FFF;
        if (address < 0x2000) {
            this.cartridge.ppuWrite(address, value);
        } else if (address < 0x3F00) {
            this.nametables[this.nametableOffset(address)] = value;
        } else {
            this.writePalette(address & 0x1F, value);
        }
    }

    private nametableOffset(address: number): number {
        const table = (address >> 10) & 0x03;
        const offset = address & 0x03FF;
        let mapped: number;
        switch (this.cartridge.getMirroring() as Mirroring) {
            case 'vertical': mapped = [0, 1, 0, 1][table]; break;
            case 'horizontal': mapped = [0, 0, 1, 1][table]; break;
            case 'four-screen': mapped = table; break;
            case 'single-screen-0': mapped = 0; break;
            case 'single-screen-1': mapped = 1; break;
            default: mapped = [0, 1, 0, 1][table]; break;
        }
        return mapped * 0x400 + offset;
    }

    private readPalette(index: number): number {
        index &= 0x1F;
        if (index >= 0x10 && (index & 0x03) === 0) index -= 0x10; // $3F10/14/18/1C mirror
        return this.paletteRam[index];
    }

    private writePalette(index: number, value: number): void {
        index &= 0x1F;
        if (index >= 0x10 && (index & 0x03) === 0) index -= 0x10;
        this.paletteRam[index] = value;
    }

    // ------------------------------------------------------------------
    // Status/NMI helpers
    // ------------------------------------------------------------------

    private setStatusFlag(flag: number, set: boolean): void {
        this.status = set ? (this.status | flag) : (this.status & ~flag);
    }

    /** NMI is edge-triggered on (vblank flag && NMI enable). */
    private checkNmi(): void {
        const line = (this.status & 0x80) !== 0 && (this.ctrl & 0x80) !== 0;
        if (line && !this.nmiLine) {
            this.logger.log(2, () => `NMI at frame ${this.frame}, scanline ${this.scanline}`);
            this.onNmi();
        }
        this.nmiLine = line;
    }
}
